import { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate } from "react-router-dom";
import { Box } from 'src/components';
import { useAuth, getChatsForCurrentUser, getChatById, initPushNotifications } from 'src/utils';
import { mapUserChat } from "src/data";

import ChatSidebarContainer from './sidebar/chat-sidebar-container';

export default function ChatContainer({ loadingChats, chatUpdates, chats, footerHeight }) {
  const { user } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [allChats, setAllChats] = useState(chats);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  useEffect(() => {
    if (userId === user?.id) {
      navigate('/');
    }
  }, [userId, user?.id]);

  useEffect(() => {
    if (user?.id) {
      initPushNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    async function loadChats() {
      try {
        const res = await getChatsForCurrentUser();
        const mapped = res.map((chat) => mapUserChat(chat, user.id, chat.type));
        setAllChats(mapped);
      } catch (err) {
        console.error("Error fetching users chats", err);
      }
    }

    loadChats();
  }, []);

  useEffect(() => {
    async function syncChatUpdates() {
      const chatIds = Object.keys(chatUpdates || {});

      // find missing chats (not already in allChats)
      const missingIds = chatIds.filter(
        (chatId) => !allChats.some((c) => c.id === chatId)
      );

      if (missingIds.length === 0) return; // nothing new to fetch

      try {
        const promises = missingIds.map(async (chatId) => {
          try {
            const res = await getChatById(chatId);
            return mapUserChat(res, user.id, res.type);
          } catch (err) {
            console.error(`Failed to fetch chat ${chatId}`, err);
            return null;
          }
        });

        const results = await Promise.all(promises);
        const valid = results.filter(Boolean);

        if (valid.length > 0) {
          setAllChats((prev) => {
            // dedupe in case of race conditions
            const existingIds = new Set(prev.map((c) => c.id));
            const newOnes = valid.filter((c) => !existingIds.has(c.id));
            return [...prev, ...newOnes];
          });
        }
      } catch (err) {
        console.error("Error syncing chat updates", err);
      }
    }

    if (chatUpdates && Object.keys(chatUpdates).length > 0) {
      syncChatUpdates();
    }
  }, [chatUpdates, user.id]);

  return (
      <Box 
        paddingTop="var(--safe-area-top)"
        display="flex" 
        width="100%" 
        height="100%" 
        bgcolor="#f9f9f9" 
        overflow="hidden">

        <ChatSidebarContainer 
          open={sidebarOpen} 
          chatUpdates={chatUpdates}
          loadingChats={loadingChats}
          chats={allChats}
          toggleSidebar={toggleSidebar}/>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Outlet 
            context={{ toggleSidebar, footerHeight, setAllChats }} />
        </Box>

      </Box>
  );
}
